import { DialogClose, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { EEventsType } from '@/lib/enums';
import { trackMixpanelEvent } from '@/lib/mixpanelClient';
import { useAppState } from '@/state/hooks/app.state.hook';
import { useLoginState } from '@/state/hooks/login.state.hook';

import { useTranslations } from 'next-intl';

export const LogoutModal: React.FC = () => {
  const { initialBoot, playerData } = useAppState();
  const { logout } = useLoginState();
  const t = useTranslations();

  const fallbackColor = initialBoot?.theme?.loginPopup?.fontColor || '#fff';
  const logoutConfig = initialBoot?.theme?.loginPopup?.logoutScreen;
  const buttonImage = logoutConfig?.buttonImage || undefined;

  // Styling applies to the whole confirmation line (player name + message);
  // only the message part comes from the configurable/translated text.
  // Empty values are simply omitted so the CSS falls back to the defaults.
  const logoutTextStyle: React.CSSProperties = {
    fontFamily: logoutConfig?.font,
    color: logoutConfig?.textColor,
    fontSize: logoutConfig?.textSize ? `${logoutConfig.textSize}px` : undefined,
    fontWeight: logoutConfig?.textWeight
  };

  // With an uploaded image the button becomes the image (no border);
  // otherwise it stays the default outlined button.
  const logoutButtonStyle: React.CSSProperties = buttonImage
    ? {
        border: 'none',
        backgroundImage: `url(${buttonImage})`,
        backgroundSize: 'cover',
        backgroundPosition: 'center',
        backgroundRepeat: 'no-repeat'
      }
    : {
        border: '1px solid',
        borderColor: fallbackColor
      };

  const handleLogout = () => {
    trackMixpanelEvent(EEventsType.PORTAL_LOGOUT, { reason: 'logout clicked' });
    logout();
  };

  return (
    <DialogContent
      hideCloseButton={!!logoutConfig?.customCloseIcon}
      className="flex max-w-[384px] flex-col items-center justify-center gap-y-6 !rounded-xl border-none bg-transparent shadow-none"
      style={{
        color: fallbackColor,
      }}
    >
      {logoutConfig?.customCloseIcon && (
        <DialogClose asChild>
          <button
            className="absolute right-4 top-4 opacity-70 transition-opacity hover:opacity-100 focus:outline-none"
            data-testid="PortalLogoutModal-CustomCloseButton"
          >
            <img
              src={logoutConfig.customCloseIcon}
              alt="close"
              className="size-[16px] object-contain"
            />
            <span className="sr-only">Close</span>
          </button>
        </DialogClose>
      )}
      <DialogHeader>
        <DialogTitle className="text-center text-2xl font-semibold">
          {t('portal.logoutModal.logout')}
        </DialogTitle>
        <div className="text-center text-base" style={logoutTextStyle}>
          {playerData?.playerName ? `${playerData.playerName}, ` : ''}
          {logoutConfig?.text || t('portal.logoutModal.disconnectConfirmation')}
        </div>
      </DialogHeader>
      <DialogClose asChild>
        <button
          style={logoutButtonStyle}
          className="w-full rounded-xl px-4 py-2.5 text-sm leading-5 hover:opacity-85"
          onClick={handleLogout}
          data-testid="PortalLogoutModal-LogoutButton"
        >
          {t('portal.logoutModal.logout')}
        </button>
      </DialogClose>
    </DialogContent>
  );
};
